import { httpsCallable } from 'firebase/functions';
import { functions } from '../config';
import {
  MasterReconcileRequest,
  MasterReconcileResponse,
  MasterHistorySaveRequest,
  MasterHistoryRequest,
  MasterHistoryResponse,
  MasterHistoryListRequest,
  MasterHistoryListResponse,
  MasterHistoryDeleteRequest,
  MasterHistoryDeleteResponse,
} from '../../../types/masterVerificationApi';

export const searchMasterReconcile = async (
  params: MasterReconcileRequest,
): Promise<MasterReconcileResponse> => {
  const fn = httpsCallable<MasterReconcileRequest, MasterReconcileResponse>(
    functions,
    'searchMasterReconcileApi',
  );
  const result = await fn(params);
  return result.data;
};

export const saveMasterHistory = async (params: MasterHistorySaveRequest): Promise<void> => {
  const fn = httpsCallable<MasterHistorySaveRequest, { status: string }>(
    functions,
    'saveMasterHistoryApi',
  );
  await fn(params);
};

export const getMasterHistory = async (
  params: MasterHistoryRequest,
): Promise<MasterHistoryResponse> => {
  const fn = httpsCallable<MasterHistoryRequest, MasterHistoryResponse>(
    functions,
    'getMasterHistoryApi',
  );
  const result = await fn(params);
  return result.data;
};

export const listMasterHistory = async (
  params: MasterHistoryListRequest,
): Promise<MasterHistoryListResponse> => {
  const fn = httpsCallable<MasterHistoryListRequest, MasterHistoryListResponse>(
    functions,
    'listMasterHistoryApi',
  );
  const result = await fn(params);
  return result.data;
};

export const deleteMasterHistory = async (
  params: MasterHistoryDeleteRequest,
): Promise<MasterHistoryDeleteResponse> => {
  const fn = httpsCallable<MasterHistoryDeleteRequest, MasterHistoryDeleteResponse>(
    functions,
    'deleteMasterHistoryApi',
  );
  const result = await fn(params);
  return result.data;
};

interface RegisterMasterDatasetRequest {
  gendrive_company_id: string;
  file_name: string;
  /** embedding の生成対象列。データセット登録に必須 */
  search_key: string;
  /**
   * 照合元項目のラベル（Cloud SQL の master_field_mappings.source_field_name）。
   * パターン紐付けを行う場合のみ指定する。pattern_id とセット（#3180）。
   */
  source_field_name?: string;
  /** 紐付け先の読取パターン。省略時は master_datasets のみ作成する（#3180） */
  pattern_id?: string;
  existing_master_dataset_id?: number;
}

interface RegisterMasterDatasetResponse {
  status: 'ok';
  master_dataset_id: number;
}

export const registerMasterDataset = async (
  params: RegisterMasterDatasetRequest,
): Promise<RegisterMasterDatasetResponse> => {
  const fn = httpsCallable<RegisterMasterDatasetRequest, RegisterMasterDatasetResponse>(
    functions,
    'registerMasterDatasetApi',
  );
  const result = await fn(params);
  return result.data;
};

interface RenameMasterFieldMappingRequest {
  gendrive_company_id: string;
  pattern_id: string;
  renames: { old_source_field_name: string; new_source_field_name: string }[];
}

/**
 * パターン項目名のリネームに追従して Cloud SQL の source_field_name を更新する（#2905）。
 * Cloud SQL 未使用企業や旧名行なしの場合はバックエンド側で no-op になる。
 */
export const renameMasterFieldMapping = async (
  params: RenameMasterFieldMappingRequest,
): Promise<void> => {
  const fn = httpsCallable<RenameMasterFieldMappingRequest, { status: 'ok' }>(
    functions,
    'renameMasterFieldMappingApi',
  );
  await fn(params);
};

interface DeleteMasterFieldMappingRequest {
  gendrive_company_id: string;
  pattern_id: string;
  source_field_name: string;
}

export const deleteMasterFieldMapping = async (
  params: DeleteMasterFieldMappingRequest,
): Promise<void> => {
  const fn = httpsCallable<DeleteMasterFieldMappingRequest, { status: 'ok' }>(
    functions,
    'deleteMasterFieldMappingApi',
  );
  await fn(params);
};

interface GetSignedUploadUrlRequest {
  gendrive_company_id: string;
  master_dataset_id: number;
  file_name: string;
  content_type: string;
  /** アップロードするCSVのレコード数（ヘッダー行を除く）。サーバ側の上限判定に使う（#3336） */
  row_count: number;
  /** CSV差し替え時の対象マスタID。既存行数が入れ替わるため合計から除く（#3336） */
  replace_master_id?: string;
}

interface GetSignedUploadUrlResponse {
  status: 'ok';
  signed_url: string;
  file_path: string;
}

export const getMasterCsvSignedUploadUrl = async (
  params: GetSignedUploadUrlRequest,
): Promise<GetSignedUploadUrlResponse> => {
  const fn = httpsCallable<GetSignedUploadUrlRequest, GetSignedUploadUrlResponse>(
    functions,
    'getMasterCsvSignedUploadUrl',
  );
  const result = await fn(params);
  return result.data;
};

interface GetSignedDownloadUrlRequest {
  gendrive_company_id: string;
  file_path: string;
}

interface GetSignedDownloadUrlResponse {
  status: 'ok';
  signed_url: string;
}

export const getMasterCsvSignedDownloadUrl = async (
  params: GetSignedDownloadUrlRequest,
): Promise<GetSignedDownloadUrlResponse> => {
  const fn = httpsCallable<GetSignedDownloadUrlRequest, GetSignedDownloadUrlResponse>(
    functions,
    'getMasterCsvSignedDownloadUrl',
  );
  const result = await fn(params);
  return result.data;
};

interface SyncMasterBatchStatusRequest {
  gendrive_company_id: string;
}

interface SyncMasterBatchStatusResponse {
  status: 'ok';
  updated_count: number;
}

/**
 * Cloud SQL csv_job_histories の最新ステータスを Firestore masters.batch_status に同期する。
 * マスタ管理画面マウント時に 1 回呼んで、Firestore onSnapshot で UI に反映される想定。
 */
export const syncMasterBatchStatus = async (
  params: SyncMasterBatchStatusRequest,
): Promise<SyncMasterBatchStatusResponse> => {
  const fn = httpsCallable<SyncMasterBatchStatusRequest, SyncMasterBatchStatusResponse>(
    functions,
    'syncMasterBatchStatusApi',
  );
  const result = await fn(params);
  return result.data;
};
